/**
 * UR Brand System — Phase 5b Designer workspace.
 *
 * Internal English only. Single screen covering:
 *   - List of assigned projects
 *   - Selected project's Designer Brief (allowlisted projection)
 *   - Flag brief issue (NO designer→customer contact)
 *   - Concept submission (reads concepts_per_round from config)
 *   - Own submitted concepts status
 *
 * Access token: ?token=<designer_session_token> OR stored in
 * localStorage("ur_designer_token"). Admin cookies are never used here.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { designerApi, adminApi } from "@/lib/brandApi";

const FLAG_CATS = [
  "Missing information", "Strategic contradiction",
  "Visual direction unclear", "Production requirement unclear",
  "Language issue", "Other",
];

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-black text-neutral-100">
      <header className="border-b border-neutral-800 px-4 sm:px-6 py-4 sticky top-0 bg-black/95 backdrop-blur z-10">
        <div className="flex items-center max-w-6xl mx-auto">
          <div className="font-black text-orange-500">Ur Designs</div>
          <div className="ml-3 text-xs text-neutral-500 hidden sm:block">Designer Workspace</div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">{children}</main>
    </div>
  );
}

export default function DesignerWorkspace() {
  const loc = useLocation();
  // Accept ?token= one time, then persist to localStorage.
  useEffect(() => {
    const sp = new URLSearchParams(loc.search);
    const q = sp.get("token");
    if (q && typeof localStorage !== "undefined") {
      localStorage.setItem("ur_designer_token", q);
      // Strip the token from the URL for safety.
      window.history.replaceState({}, "", loc.pathname);
    }
  }, [loc]);

  const api = useMemo(() => designerApi(), []);
  const [me, setMe] = useState(null);
  const [projects, setProjects] = useState([]);
  const [config, setConfig] = useState(null);
  const [err, setErr] = useState("");
  const [picked, setPicked] = useState("");

  const refresh = async () => {
    try {
      const [m, p] = await Promise.all([api.get("/me"), api.get("/projects")]);
      setMe(m.data); setProjects(p.data || []); setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Unauthorized — ask an admin for a fresh designer link.");
    }
    try {
      const cfg = await adminApi().get("/admin/brand-system/config");
      setConfig(cfg.data);
    } catch { /* non-fatal: admins and designers have different auth */ }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  if (err) return <Shell>
    <div className="p-6 border border-red-500/40 text-red-200 rounded-lg" data-testid="designer-unauthorized">{err}</div>
  </Shell>;
  if (!me) return <Shell><p className="text-neutral-400">Loading…</p></Shell>;

  return (
    <Shell>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs text-neutral-500 tracking-widest">DESIGNER</div>
          <div className="font-black text-xl">{me.display_name || me.email}</div>
        </div>
        <button onClick={() => {
          localStorage.removeItem("ur_designer_token");
          window.location.reload();
        }} className="text-xs text-neutral-500 hover:text-neutral-300" data-testid="designer-signout">
          Sign out
        </button>
      </div>

      {!picked && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="designer-projects">
          {projects.length === 0 && <p className="text-neutral-500">No projects assigned.</p>}
          {projects.map((p) => (
            <button key={p.project_id} onClick={() => setPicked(p.project_id)}
              data-testid={`designer-project-${p.project_id}`}
              className="text-left p-4 border border-neutral-800 rounded-lg hover:border-orange-500 bg-neutral-950">
              <div className="font-black text-lg">{p.business_name || "(no name)"}</div>
              <div className="text-xs text-neutral-500 mt-1">{p.project_status}</div>
              <div className="text-xs text-neutral-500 mt-0.5">Role: {p.assignment_role}</div>
            </button>
          ))}
        </div>
      )}

      {picked && <ProjectView projectId={picked} onBack={() => setPicked("")} config={config} />}
    </Shell>
  );
}

function ProjectView({ projectId, onBack, config }) {
  const api = designerApi();
  const [brief, setBrief] = useState(null);
  const [concepts, setConcepts] = useState([]);
  const [tab, setTab] = useState("brief");
  const [err, setErr] = useState("");
  const refresh = async () => {
    try {
      const b = await api.get(`/projects/${projectId}/brief`);
      setBrief(b.data); setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Brief not available.");
    }
    try {
      const c = await api.get(`/projects/${projectId}/concepts`);
      setConcepts(c.data || []);
    } catch {}
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [projectId]);

  return (
    <div data-testid="designer-project-view">
      <button onClick={onBack} data-testid="designer-back" className="text-xs text-neutral-500 mb-4">← Back</button>
      {err && <div className="p-4 border border-red-500/40 text-red-200 rounded mb-4" data-testid="brief-error">{err}</div>}
      <nav className="flex gap-2 mb-6">
        <button onClick={() => setTab("brief")} data-testid="designer-tab-brief"
          className={`px-3 py-1.5 text-xs tracking-widest uppercase rounded-md ${tab === "brief" ? "bg-orange-500 text-black font-bold" : "text-neutral-400"}`}>Brief</button>
        <button onClick={() => setTab("submit")} data-testid="designer-tab-submit"
          className={`px-3 py-1.5 text-xs tracking-widest uppercase rounded-md ${tab === "submit" ? "bg-orange-500 text-black font-bold" : "text-neutral-400"}`}>Submit concept</button>
        <button onClick={() => setTab("mine")} data-testid="designer-tab-mine"
          className={`px-3 py-1.5 text-xs tracking-widest uppercase rounded-md ${tab === "mine" ? "bg-orange-500 text-black font-bold" : "text-neutral-400"}`}>My concepts</button>
      </nav>
      {tab === "brief" && brief && <BriefView brief={brief} />}
      {tab === "submit" && brief && <SubmitConcept brief={brief} config={config} onDone={refresh} />}
      {tab === "mine" && <MyConcepts concepts={concepts} />}
    </div>
  );
}

function BriefView({ brief }) {
  const api = designerApi();
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagCat, setFlagCat] = useState("");
  const [flagNote, setFlagNote] = useState("");
  const [issues, setIssues] = useState([]);
  const [busy, setBusy] = useState(false);

  const CAT_MAP = {
    "Missing information": "missing_information",
    "Strategic contradiction": "strategic_contradiction",
    "Visual direction unclear": "visual_direction_unclear",
    "Production requirement unclear": "production_requirement_unclear",
    "Language issue": "language_issue",
    "Other": "other",
  };

  const loadIssues = async () => {
    try {
      const r = await api.get(`/projects/${brief.project_id}/brief-issues`);
      setIssues(r.data || []);
    } catch { /* non-fatal */ }
  };
  useEffect(() => { loadIssues(); /* eslint-disable-next-line */ }, [brief.project_id]);

  const sendFlag = async () => {
    if (!flagCat) return;
    setBusy(true);
    try {
      await api.post(`/projects/${brief.project_id}/brief-issues`, {
        category: CAT_MAP[flagCat] || "other",
        note: flagNote,
      });
      toast.success("Issue sent to Ur Designs reviewer.");
      setFlagOpen(false); setFlagCat(""); setFlagNote("");
      loadIssues();
    } catch (e) {
      // Preserve the designer's typed note on failure.
      toast.error(e?.response?.data?.detail || "Could not send issue. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="brief-view">
      <section className="p-4 border border-neutral-800 rounded-lg">
        <div className="text-xs text-neutral-500 tracking-widest mb-2">ASSIGNMENT</div>
        <div className="text-xl font-black">{brief.business_name}</div>
        <p className="text-sm text-neutral-300 mt-2">{brief.strategic_summary}</p>
      </section>
      <section className="p-4 border border-neutral-800 rounded-lg">
        <div className="text-xs text-neutral-500 tracking-widest mb-2">CREATIVE DIRECTION</div>
        <p className="text-sm text-neutral-300">{brief.selected_territory_summary}</p>
        <div className="mt-3 grid grid-cols-3 gap-1">
          {(brief.reference_visuals || []).slice(0, 9).map((v) => (
            v.image_url ? <img key={v.visual_id} src={v.image_url} alt="" className="aspect-square object-cover rounded" />
                        : <div key={v.visual_id} className="aspect-square bg-neutral-900 rounded" />
          ))}
        </div>
      </section>
      <section className="p-4 border border-neutral-800 rounded-lg">
        <div className="text-xs text-neutral-500 tracking-widest mb-2">PRODUCTION REALITY</div>
        <div className="text-xs text-neutral-500 mb-1">Deliverables:</div>
        <ul className="text-sm text-neutral-300 mb-3">
          {(brief.deliverables || []).map((d) => <li key={d}>• {d}</li>)}
        </ul>
        <div className="text-xs text-neutral-500 mb-1">Production contacts (literal content for artwork only):</div>
        <ul className="text-sm text-neutral-300" data-testid="brief-production-contacts">
          {(brief.production_contacts || []).map((p, i) => (
            <li key={i}><span className="text-neutral-500 text-xs">{p.label}:</span> {p.value}</li>
          ))}
        </ul>
        <div className="mt-3 text-[11px] text-neutral-500">
          These are provided to render on the artwork. Do not use them to contact the customer.
        </div>
      </section>
      <div className="lg:col-span-3 flex justify-end mt-2">
        <button onClick={() => setFlagOpen(true)} data-testid="btn-flag-brief"
          className="px-3 py-2 border border-neutral-700 rounded text-sm">
          Flag brief issue
        </button>
      </div>
      {flagOpen && (
        <div className="lg:col-span-3 p-4 border border-amber-500/30 bg-amber-500/5 rounded-lg" data-testid="flag-panel">
          <div className="text-sm font-bold mb-3">Flag a brief issue</div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {FLAG_CATS.map((c) => (
              <label key={c} className="flex items-center gap-2 text-sm">
                <input type="radio" checked={flagCat === c} onChange={() => setFlagCat(c)}
                  data-testid={`flag-cat-${c.slice(0, 6)}`} />
                <span>{c}</span>
              </label>
            ))}
          </div>
          <textarea placeholder="Short note to the reviewer" value={flagNote}
            onChange={(e) => setFlagNote(e.target.value)}
            className="w-full bg-neutral-900 border border-neutral-800 rounded p-2 text-sm" />
          <div className="mt-3 flex gap-2">
            <button onClick={() => setFlagOpen(false)} className="px-3 py-2 border border-neutral-700 rounded text-sm">Cancel</button>
            <button onClick={sendFlag} disabled={!flagCat || busy}
              data-testid="flag-send"
              className="px-3 py-2 bg-orange-500 text-black font-bold rounded text-sm disabled:opacity-30">
              {busy ? "Sending…" : "Send issue"}
            </button>
          </div>
        </div>
      )}
      {issues.length > 0 && (
        <div className="lg:col-span-3 p-4 border border-neutral-800 rounded-lg" data-testid="brief-issues-list">
          <div className="text-xs text-neutral-500 tracking-widest mb-2">YOUR SUBMITTED ISSUES</div>
          {issues.map((i) => (
            <div key={i.brief_issue_id} className="flex items-center gap-3 py-2 border-b border-neutral-900 text-sm last:border-0"
              data-testid={`brief-issue-${i.brief_issue_id}`}>
              <span className="text-neutral-500">{i.category}</span>
              <span className="flex-1 truncate">{i.note || "(no note)"}</span>
              <span className={`px-2 py-0.5 text-[10px] rounded border ${
                i.status === "resolved" ? "border-green-500/40 text-green-300"
                                        : "border-amber-500/40 text-amber-300"
              }`}>{i.status.toUpperCase()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SubmitConcept({ brief, config, onDone }) {
  const api = designerApi();
  const slots = (config?.concepts_per_round) || 2;
  const [slot, setSlot] = useState(1);
  const [draft, setDraft] = useState({
    primary_logo_asset_url: "",
    color_direction: "",
    typography_direction: "",
    rationale: "",
    originality_source: "original_by_designer",
    originality_note: "",
    font_name: "", font_license_status: "ur_designs_licensed",
  });
  const submit = async () => {
    try {
      await api.post(`/projects/${brief.project_id}/concepts`, {
        designer_brief_id: brief.designer_brief_id,
        round_number: 1,
        concept_slot: slot,
        primary_logo_asset_url: draft.primary_logo_asset_url,
        color_direction: draft.color_direction,
        typography_direction: draft.typography_direction,
        rationale: draft.rationale,
        applications: [],
        originality_source: draft.originality_source,
        originality_note: draft.originality_note,
        font_declarations: draft.font_name ? [{
          font_name: draft.font_name,
          license_status: draft.font_license_status,
        }] : [],
      });
      toast.success("Concept submitted");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "submit failed"); }
  };

  return (
    <div className="max-w-2xl space-y-3" data-testid="submit-concept">
      <div className="flex gap-2">
        <span className="text-xs text-neutral-500 tracking-widest self-center">SLOT</span>
        {Array.from({ length: slots }).map((_, i) => (
          <button key={i} onClick={() => setSlot(i + 1)}
            data-testid={`slot-${i + 1}`}
            className={`px-3 py-1 text-sm rounded ${slot === i + 1 ? "bg-orange-500 text-black font-bold" : "border border-neutral-700"}`}>
            {i + 1}
          </button>
        ))}
      </div>
      <input placeholder="Primary logo asset URL" value={draft.primary_logo_asset_url}
        onChange={(e) => setDraft({ ...draft, primary_logo_asset_url: e.target.value })}
        data-testid="sc-logo-url"
        className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm" />
      <input placeholder="Color direction" value={draft.color_direction}
        onChange={(e) => setDraft({ ...draft, color_direction: e.target.value })}
        data-testid="sc-color"
        className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm" />
      <input placeholder="Typography direction" value={draft.typography_direction}
        onChange={(e) => setDraft({ ...draft, typography_direction: e.target.value })}
        data-testid="sc-type"
        className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm" />
      <textarea placeholder="Short rationale" value={draft.rationale}
        onChange={(e) => setDraft({ ...draft, rationale: e.target.value })}
        className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm" />
      <div className="pt-2 border-t border-neutral-800">
        <div className="text-xs text-neutral-500 mb-2">Originality / source</div>
        <select value={draft.originality_source}
          onChange={(e) => setDraft({ ...draft, originality_source: e.target.value })}
          data-testid="sc-originality"
          className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm">
          <option value="original_by_designer">Original by designer</option>
          <option value="licensed_asset">Licensed asset</option>
          <option value="ur_designs_owned">Ur Designs owned</option>
          <option value="composed_from_references">Composed from references</option>
        </select>
      </div>
      <div className="pt-2 border-t border-neutral-800">
        <div className="text-xs text-neutral-500 mb-2">Font licensing (required if typography set)</div>
        <input placeholder="Font name" value={draft.font_name}
          onChange={(e) => setDraft({ ...draft, font_name: e.target.value })}
          data-testid="sc-font-name"
          className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm mb-2" />
        <select value={draft.font_license_status}
          onChange={(e) => setDraft({ ...draft, font_license_status: e.target.value })}
          data-testid="sc-font-license"
          className="w-full bg-neutral-900 border border-neutral-800 rounded px-2 py-2 text-sm">
          <option value="ur_designs_licensed">Ur Designs licensed</option>
          <option value="customer_will_license">Customer will license</option>
          <option value="open_source">Open source</option>
          <option value="not_resolved">Not resolved</option>
        </select>
      </div>
      <button onClick={submit} data-testid="sc-submit"
        className="w-full px-3 py-3 bg-orange-500 text-black font-bold rounded">
        Submit concept
      </button>
    </div>
  );
}

function MyConcepts({ concepts }) {
  return (
    <div className="space-y-2 max-w-3xl" data-testid="my-concepts">
      {concepts.length === 0 && <p className="text-neutral-500 text-sm">No submissions yet.</p>}
      {concepts.map((c) => (
        <div key={c.concept_id} className="p-3 border border-neutral-800 rounded-lg">
          <div className="flex items-center gap-3 text-xs">
            <span>Round {c.round_number} · Slot {c.concept_slot} · v{c.version_number}</span>
            <span className="ml-auto text-neutral-500">{c.status}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
